// Input → engine dispatch for GameScene. Pure functions over a tiny mutable
// "view" handle (selection, pencil mode, etc.); the scene owns the handle
// and calls these helpers from its InputHandler callbacks. Split out so the
// scene class stays inside the 200-line cap and the dispatch table is
// straightforwardly testable.

import {
  enterDigit,
  erase as engineErase,
  redo as engineRedo,
  togglePencil,
  undo as engineUndo,
  type GameStateData,
} from '../game/GameState.js';
import { BOARD_COLS, BOARD_ROWS, CENTER_CELL_INDEX } from '../utils/constants.js';
import type { NonEmptyDigit } from '../game/types.js';
import type { ArrowDirection } from '../input/InputHandler.js';

/** True iff every user cell equals the puzzle's solution. */
export function isPuzzleComplete(state: GameStateData): boolean {
  const { user, puzzle } = state;
  for (let i = 0; i < user.length; i += 1) {
    if (user[i] !== puzzle.solution[i]) return false;
  }
  return true;
}

/**
 * Move the selected cell index in the given direction with clamping at the
 * board edges. If no cell is currently selected, the first arrow sets the
 * selection to the centre cell, matching newspaper sudoku convention.
 * Pure — exposed for unit tests.
 */
export function moveSelection(
  selectedIndex: number | null,
  direction: ArrowDirection,
): number {
  if (selectedIndex === null) return CENTER_CELL_INDEX;
  const row = Math.floor(selectedIndex / BOARD_COLS);
  const col = selectedIndex % BOARD_COLS;
  let nextRow = row;
  let nextCol = col;
  if (direction === 'up') nextRow = Math.max(0, row - 1);
  else if (direction === 'down') nextRow = Math.min(BOARD_ROWS - 1, row + 1);
  else if (direction === 'left') nextCol = Math.max(0, col - 1);
  else if (direction === 'right') nextCol = Math.min(BOARD_COLS - 1, col + 1);
  return nextRow * BOARD_COLS + nextCol;
}

/**
 * Dispatch a digit entry. Returns `{ applied, wasError }` so the caller can
 * decide whether to trigger error-flash bookkeeping. In pencil mode the
 * digit toggles the candidate; in normal mode it places the digit.
 */
export interface DigitDispatchResult {
  readonly applied: boolean;
  readonly wasError: boolean;
}

export function dispatchDigit(
  state: GameStateData,
  selectedIndex: number | null,
  digit: NonEmptyDigit,
  pencilMode: boolean,
): DigitDispatchResult {
  if (selectedIndex === null) return { applied: false, wasError: false };
  if (pencilMode) {
    const applied = togglePencil(state, selectedIndex, digit);
    return { applied, wasError: false };
  }
  const applied = enterDigit(state, selectedIndex, digit);
  if (!applied) return { applied: false, wasError: false };
  const wasError = digit !== state.puzzle.solution[selectedIndex];
  return { applied, wasError };
}

export function dispatchErase(
  state: GameStateData,
  selectedIndex: number | null,
): boolean {
  if (selectedIndex === null) return false;
  return engineErase(state, selectedIndex);
}

export function dispatchUndo(state: GameStateData): boolean {
  return engineUndo(state);
}

export function dispatchRedo(state: GameStateData): boolean {
  return engineRedo(state);
}
